<script setup lang="ts">
import * as THREE from 'three'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
} from 'vue'
import { usePotreeEngine } from '../hooks'
import {
  applySpaceMouseNavigationProfile,
  applySpaceMouseSecondaryControlSelection,
  buildSpaceMouseSecondaryControlGroups,
  createSpaceMouseNavigationState,
  readSpaceMouseNavigationState,
  type SpaceMouseControllerLike,
  type SpaceMouseNavigationProfile,
  type SpaceMouseNavigationState,
} from '../utils/spaceMouseNavigation'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import SceneViewCard from './SceneViewCard.vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { resolvePointCloudInputPath } from '../utils/assetPath'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import {
  CameraMode,
  type CameraRig,
  ClipOverlayPlugin,
  ClipPlugin,
  type ClipService,
  ClipTask,
  type ControlMode,
  createMeshContentAdapter,
  type GaussianSplatHandle,
  GaussianSplatPlugin,
  HttpAssetSource,
  type PointCloudPlugin,
  type PotreeEngine,
  TilesPlugin,
  type TilesetHandle,
  createBoxVolume,
  presets,
  type ViewHandle,
  type ViewPreset,
  type ViewRect,
} from '@sharefe/share3d-engine'
import { resolvePresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { TILES_FIXTURES } from '../examples/tilesFixtures'

type ManagedViewId = 'primary' | 'inspect' | 'top'

interface ManagedViewDefinition {
  id: ManagedViewId
  title: string
  subtitle: string
  defaultControlMode: ControlMode
  defaultCameraMode: CameraMode
  defaultPreset: ViewPreset
  defaultSpaceMouseNavigationProfile: SpaceMouseNavigationProfile
  layerMask: number
  accent: string
  overlayHint: string
}

interface ManagedViewState {
  cameraMode: CameraMode
  controlMode: ControlMode
  layerMask: number
  spaceMouseNavigation: SpaceMouseNavigationState
}

interface ManagedViewControlManager {
  activeMode: ControlMode
  setMode(mode: ControlMode): void
  getController(mode: string): unknown
}

const DEFAULT_POINT_CLOUD_MODEL = '/converted_r/metadata.json'
const BASE_LAYER_MASK = 1 << 0
const INSPECT_LAYER_MASK = 1 << 1
const TOP_LAYER_MASK = 1 << 2
const PRIMARY_VIEW_CLIP_GROUP_ID = 'multiViewportPrimaryClip'

function detectEmbeddedFrame(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

const VIEW_OPTIONS: Array<{
  value: ViewPreset
  label: string
}> = [
  { value: 'top', label: '俯视' },
  { value: 'bottom', label: '仰视' },
  { value: 'front', label: '前视' },
  { value: 'back', label: '后视' },
  { value: 'left', label: '左视' },
  { value: 'right', label: '右视' },
]

const CONTROL_MODE_OPTIONS: Array<{
  value: ControlMode
  label: string
  detail: string
}> = [
  { value: 'orbit', label: '轨道', detail: '围绕目标点观察，适合作为主视图' },
  { value: 'earth', label: '巡检', detail: '按命中点旋转，适合 inspect 视角' },
  { value: 'map', label: '地图', detail: '平移优先，适合俯视图浏览' },
]

const VIEW_CONTROL_MODE_OPTIONS = [
  CONTROL_MODE_OPTIONS[0],
  {
    value: 'spacemouse' as ControlMode,
    label: 'SpaceMouse',
    detail: 'Object-center rotation with mouse and 3D mouse support',
  },
  ...CONTROL_MODE_OPTIONS.slice(1),
] satisfies Array<{
  value: ControlMode
  label: string
  detail: string
}>

const VIEW_DEFINITIONS: ManagedViewDefinition[] = [
  {
    id: 'primary',
    title: '主视图',
    subtitle: '默认大视口，保留基础层，并额外演示仅作用于主视图的 scoped 裁切。',
    defaultControlMode: 'orbit',
    defaultCameraMode: CameraMode.Perspective,
    defaultPreset: 'front',
    defaultSpaceMouseNavigationProfile: 'mouse-only',
    layerMask: BASE_LAYER_MASK,
    accent: '#7bd9ff',
    overlayHint: '仅主视图裁切',
  },
  {
    id: 'inspect',
    title: 'Inspect View',
    subtitle: '附加橙色巡检路径，只显示主层 + Inspect 层。',
    defaultControlMode: 'spacemouse',
    defaultCameraMode: CameraMode.Perspective,
    defaultPreset: 'right',
    defaultSpaceMouseNavigationProfile: 'hybrid',
    layerMask: BASE_LAYER_MASK | INSPECT_LAYER_MASK,
    accent: '#ffb36a',
    overlayHint: '额外显示橙色巡检层',
  },
  {
    id: 'top',
    title: '俯视图',
    subtitle: '默认正交 + 地图控制，额外显示青色俯视标记层。',
    defaultControlMode: 'map',
    defaultCameraMode: CameraMode.Orthographic,
    defaultPreset: 'top',
    defaultSpaceMouseNavigationProfile: 'mouse-only',
    layerMask: BASE_LAYER_MASK | TOP_LAYER_MASK,
    accent: '#73f4d1',
    overlayHint: '额外显示青色俯视层',
  },
]

const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
const stageShellRef = useTemplateRef<HTMLDivElement>('stageShellRef')
const viewElements = new Map<ManagedViewId, HTMLElement>()
const helperResources: Array<THREE.BufferGeometry | THREE.Material> = []
let helperRoot: THREE.Group | null = null
let resizeObserver: ResizeObserver | null = null
let resizeRaf = 0
let detachViewportSyncListeners: (() => void) | null = null
const cleanupTasks: Array<() => void> = []

const pointCloudInput = ref(DEFAULT_POINT_CLOUD_MODEL)
const pointCloudLoading = ref(false)
const pointCloudLoadError = ref('')
const pointCloudAssetCount = ref(0)

type ExtraModelSlot = 'B' | 'C'

interface ExtraModelSlotDefinition {
  readonly slot: ExtraModelSlot
  readonly label: string
  readonly layer: number
  readonly offset: readonly [number, number, number]
  readonly accent: string
}

/**
 * 附加模型实例：同一份点云资产重复加载并按图层分配到不同视口。
 *
 * 三个 View 的 layerMask 分别是 L0 / L0+L1 / L0+L2，因此把 B 放到 L1、C 放到 L2 后，
 * 主视图只看得到 A，Inspect View 额外看到 B，俯视图额外看到 C。
 */
const EXTRA_MODEL_SLOTS: readonly ExtraModelSlotDefinition[] = [
  { slot: 'B', label: '模型 B', layer: INSPECT_LAYER_MASK, offset: [2.4, 0, 0], accent: '#ffb36a' },
  { slot: 'C', label: '模型 C', layer: TOP_LAYER_MASK, offset: [0, 0, 2.4], accent: '#73f4d1' },
]

interface ExtraModelState {
  readonly assetId: string
  visible: boolean
}

const extraModels = reactive<Record<ExtraModelSlot, ExtraModelState | null>>({
  B: null,
  C: null,
})
const extraModelError = ref('')

const primaryClipActive = ref(false)
const activeViewId = ref<ManagedViewId>('primary')
const isEmbeddedFrame = ref(detectEmbeddedFrame())
// 移动端视口下默认收起右侧面板，避免遮挡多视口画布
const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches
const isPanelCollapsed = ref(isMobileViewport)
const appliedSpaceMouseDefaults = new Set<ManagedViewId>()

const plugins = [
  ...presets.pointCloudOnly(),
  new ClipPlugin(),
  new ClipOverlayPlugin(),
  // 多视口回归需要覆盖 3DGS 与 3D Tiles 的加载 / 显隐 / 卸载，而不只是点云。
  new GaussianSplatPlugin(),
  // Tiles 只参与俯视图：用插件级 viewIds 做 per-view 作用域，避免逐帧改写内容网格 layer。
  new TilesPlugin({
    viewIds: ['top'],
    contentAdapters: [createMeshContentAdapter()],
    source: new HttpAssetSource(),
  }),
]

const viewStates = reactive<Record<ManagedViewId, ManagedViewState>>({
  primary: {
    cameraMode: CameraMode.Perspective,
    controlMode: 'orbit',
    layerMask: BASE_LAYER_MASK,
    spaceMouseNavigation: createSpaceMouseNavigationState('mouse-only'),
  },
  inspect: {
    cameraMode: CameraMode.Perspective,
    controlMode: 'spacemouse',
    layerMask: BASE_LAYER_MASK | INSPECT_LAYER_MASK,
    spaceMouseNavigation: createSpaceMouseNavigationState('hybrid'),
  },
  top: {
    cameraMode: CameraMode.Orthographic,
    controlMode: 'map',
    layerMask: BASE_LAYER_MASK | TOP_LAYER_MASK,
    spaceMouseNavigation: createSpaceMouseNavigationState('mouse-only'),
  },
})

/**
 * 该 route 的插件闭包同时含 corePlugins() 的 webgl2-only 主体与追加的 Clip 插件，
 * 因此仍然阻塞 WebGPU；卡片继续展示真实阻塞项而不是隐藏选项。
 */
const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudOnly',
  extras: ['ClipPlugin', 'ClipOverlayPlugin'],
})

const { engine, init, actualBackend } = usePotreeEngine(containerRef, {
  backend: graphicsCard.requestedBackend.value,
  plugins,
  background: '#06111d',
  pointBudget: 1e7,
  fov: 60,
  cameraMode: CameraMode.Perspective,
})

const activeViewDefinition = computed(() => {
  return getViewDefinition(activeViewId.value)
})

const extraModelCountLabel = computed(() => {
  const count = EXTRA_MODEL_SLOTS.filter((item) => extraModels[item.slot] !== null).length
  return `${count} / ${EXTRA_MODEL_SLOTS.length}`
})

const statusItems = computed(() => {
  return [
    { label: '当前 active', value: activeViewDefinition.value.title },
    { label: '活动控制器', value: formatControlModeLabel(viewStates[activeViewId.value].controlMode) },
    { label: '已加载点云', value: pointCloudAssetCount.value },
    { label: '视图数量', value: VIEW_DEFINITIONS.length },
    { label: '主视图裁切', value: primaryClipActive.value ? '仅 primary 生效' : '未启用' },
  ]
})

const pointCloudLoaderHint = computed(() => {
  if (pointCloudLoadError.value) {
    return `加载失败：${pointCloudLoadError.value}`
  }

  return '默认会加载示例点云，并自动添加一个只对主视图生效的 box clip。页面右侧三张卡片分别控制三个 view。'
})

function getViewDefinition(viewId: ManagedViewId): ManagedViewDefinition {
  return VIEW_DEFINITIONS.find((item) => item.id === viewId) ?? VIEW_DEFINITIONS[0]
}

function getViewHandle(viewId: ManagedViewId): ViewHandle | undefined {
  return engine.value?.views.get(viewId)
}

function getViewRig(viewId: ManagedViewId): CameraRig | null {
  return getViewHandle(viewId)?.getSlot<CameraRig>('cameraRig') ?? null
}

function getViewControlManager(viewId: ManagedViewId): ManagedViewControlManager | null {
  return getViewHandle(viewId)?.getSlot<ManagedViewControlManager>('controlManager') ?? null
}

function getViewSpaceMouseController(viewId: ManagedViewId): SpaceMouseControllerLike | null {
  const controller = getViewControlManager(viewId)?.getController('spacemouse') as
    | SpaceMouseControllerLike
    | undefined

  return controller ?? null
}

function getPointCloudPlugin(): PointCloudPlugin | null {
  return engine.value?.plugins.get<PointCloudPlugin>('pointCloud') ?? null
}

function getClipService(): ClipService | null {
  const engineInstance = engine.value

  if (!engineInstance?.context.hasService('ClipService')) {
    return null
  }

  return engineInstance.context.getService('ClipService') as ClipService
}

function setViewElement(
  viewId: ManagedViewId,
  element: Element | { $el?: Element | null } | null,
) {
  const resolvedElement =
    element instanceof HTMLElement ?
      element :
      element && typeof element === 'object' && '$el' in element ?
        element.$el :
        null

  if (resolvedElement instanceof HTMLElement) {
    viewElements.set(viewId, resolvedElement)
    return
  }

  viewElements.delete(viewId)
}

function toManagedViewId(value: string | null | undefined): ManagedViewId {
  if (value === 'inspect' || value === 'top') {
    return value
  }

  return 'primary'
}

function formatControlModeLabel(mode: ControlMode): string {
  return VIEW_CONTROL_MODE_OPTIONS.find((item) => item.value === mode)?.label ?? mode
}

function formatCameraModeLabel(mode: CameraMode): string {
  return mode === CameraMode.Perspective ? '透视' : '正交'
}

function formatLayerMask(layerMask: number): string {
  if (!Number.isFinite(layerMask) || layerMask <= 0) {
    return '无可见层'
  }

  const labels: string[] = []
  for (let bit = 0; bit < 8; bit += 1) {
    if ((layerMask & (1 << bit)) !== 0) {
      labels.push(`L${bit}`)
    }
  }

  return labels.length > 0 ? labels.join(' + ') : '无可见层'
}

function syncPrimaryClipState() {
  primaryClipActive.value =
    getClipService()?.volumes.some(
      (volume) => volume.groupId === PRIMARY_VIEW_CLIP_GROUP_ID && volume.viewId === 'primary',
    ) ?? false
}

function buildPrimaryScopedClipBox(bounds: THREE.Box3) {
  if (bounds.isEmpty()) {
    return null
  }

  const center = bounds.getCenter(new THREE.Vector3())
  const size = bounds.getSize(new THREE.Vector3())
  if (size.lengthSq() === 0) {
    return null
  }

  const halfBoxWidth = Math.max(size.x * 0.5, 0.8)
  const position = center.clone().add(
    new THREE.Vector3(size.x * 0.25, 0, 0),
  )
  const scale = new THREE.Vector3(
    halfBoxWidth,
    Math.max(size.y * 1.08, 0.8),
    Math.max(size.z * 1.08, 0.8),
  )

  const volume = createBoxVolume(position, scale)
  volume.groupId = PRIMARY_VIEW_CLIP_GROUP_ID
  volume.viewId = 'primary'
  volume.clipTask = ClipTask.SHOW_INSIDE
  return volume
}

function clearPrimaryScopedClipVolume() {
  getClipService()?.setGroupClipTask(PRIMARY_VIEW_CLIP_GROUP_ID, [])
  syncPrimaryClipState()
}

function applyPrimaryScopedClipVolume(bounds: THREE.Box3 | null) {
  const clipService = getClipService()
  if (!clipService) {
    primaryClipActive.value = false
    return
  }

  if (!bounds || bounds.isEmpty()) {
    clipService.setGroupClipTask(PRIMARY_VIEW_CLIP_GROUP_ID, [])
    syncPrimaryClipState()
    return
  }

  const volume = buildPrimaryScopedClipBox(bounds)
  clipService.setGroupClipTask(PRIMARY_VIEW_CLIP_GROUP_ID, volume ? [volume] : [])
  syncPrimaryClipState()
}

function getViewCardBadge(viewId: ManagedViewId): string {
  const activeLabel = activeViewId.value === viewId ? '当前活动' : '待命'
  return `${activeLabel} / ${formatLayerMask(viewStates[viewId].layerMask)}`
}

function getViewportTagMeta(viewId: ManagedViewId): string {
  const state = viewStates[viewId]
  return `${formatCameraModeLabel(state.cameraMode)} / ${formatControlModeLabel(state.controlMode)} / ${formatLayerMask(state.layerMask)}`
}

function requiresPerspectiveControlMode(mode: ControlMode): boolean {
  return mode === 'first-person' || mode === 'legacy-first-person' || mode === 'fly'
}

function roundRect(rect: ViewRect): ViewRect {
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  }
}

function clearTrackedResources() {
  for (const resource of helperResources.splice(0)) {
    resource.dispose()
  }
}

function clearHelperRoot() {
  if (!helperRoot) {
    return
  }

  helperRoot.clear()
  clearTrackedResources()
}

function trackHelperMaterial<T extends THREE.Material>(material: T): T {
  helperResources.push(material)
  return material
}

function trackHelperGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
  helperResources.push(geometry)
  return geometry
}

function createLine(points: THREE.Vector3[], color: string): THREE.Line {
  const geometry = trackHelperGeometry(new THREE.BufferGeometry().setFromPoints(points))
  const material = trackHelperMaterial(new THREE.LineBasicMaterial({ color }))
  return new THREE.Line(geometry, material)
}

function createHelperScene(engineInstance: PotreeEngine) {
  const root = engineInstance.sceneGraph.registerRoot('multiViewportExample')
  helperRoot = root
  clearHelperRoot()

  const axes = new THREE.AxesHelper(3.5)
  axes.layers.set(0)
  root.add(axes)

  const baseBoxes = [
    { position: new THREE.Vector3(-2.8, -1.4, 0.8), size: [1.2, 0.9, 1.6], color: '#5bc0ff' },
    { position: new THREE.Vector3(0, 0, 1.15), size: [1.4, 1.4, 2.3], color: '#8df2c9' },
    { position: new THREE.Vector3(2.4, 1.2, 0.65), size: [0.9, 1.6, 1.3], color: '#d4e7ff' },
  ]

  for (const item of baseBoxes) {
    const geometry = trackHelperGeometry(
      new THREE.BoxGeometry(item.size[0], item.size[1], item.size[2]),
    )
    const material = trackHelperMaterial(
      new THREE.MeshBasicMaterial({
        color: item.color,
        wireframe: true,
        transparent: true,
        opacity: 0.9,
      }),
    )
    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.copy(item.position)
    mesh.layers.set(0)
    root.add(mesh)
  }

  const inspectLine = createLine(
    [
      new THREE.Vector3(-3.4, 1.6, 0.6),
      new THREE.Vector3(-1.9, 2.1, 1.4),
      new THREE.Vector3(0.3, 1.6, 1.9),
      new THREE.Vector3(2.1, 2.2, 1.2),
      new THREE.Vector3(3.2, 1.4, 0.7),
    ],
    '#ffb36a',
  )
  inspectLine.layers.set(1)
  root.add(inspectLine)

  const inspectPointGeometry = trackHelperGeometry(new THREE.SphereGeometry(0.12, 16, 12))
  const inspectPointMaterial = trackHelperMaterial(
    new THREE.MeshBasicMaterial({ color: '#ffb36a' }),
  )
  for (const point of [
    new THREE.Vector3(-1.9, 2.1, 1.4),
    new THREE.Vector3(0.3, 1.6, 1.9),
    new THREE.Vector3(2.1, 2.2, 1.2),
  ]) {
    const marker = new THREE.Mesh(inspectPointGeometry, inspectPointMaterial)
    marker.position.copy(point)
    marker.layers.set(1)
    root.add(marker)
  }

  for (const point of [
    new THREE.Vector3(-2.8, -2.6, 0.05),
    new THREE.Vector3(0, -2.2, 0.05),
    new THREE.Vector3(2.8, -2.7, 0.05),
  ]) {
    const columnGeometry = trackHelperGeometry(new THREE.CylinderGeometry(0.08, 0.08, 1.8, 16))
    const columnMaterial = trackHelperMaterial(
      new THREE.MeshBasicMaterial({
        color: '#73f4d1',
        transparent: true,
        opacity: 0.9,
      }),
    )
    const column = new THREE.Mesh(columnGeometry, columnMaterial)
    column.position.copy(point).add(new THREE.Vector3(0, 0, 0.9))
    column.layers.set(2)
    root.add(column)

    const ringGeometry = trackHelperGeometry(new THREE.RingGeometry(0.18, 0.28, 24))
    const ringMaterial = trackHelperMaterial(
      new THREE.MeshBasicMaterial({
        color: '#73f4d1',
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.72,
      }),
    )
    const ring = new THREE.Mesh(ringGeometry, ringMaterial)
    ring.position.copy(point).add(new THREE.Vector3(0, 0, 0.04))
    ring.layers.set(2)
    root.add(ring)
  }
}

function getSceneFitBox(): THREE.Box3 {
  const box = new THREE.Box3()
  const engineInstance = engine.value

  if (!engineInstance) {
    return box
  }

  const assetBounds = engineInstance.context.sceneBounds.getWorldBoundingBox({
    includeInvisible: true,
    purpose: 'fit',
  })
  if (!assetBounds.isEmpty()) {
    box.union(assetBounds)
  }

  if (helperRoot) {
    helperRoot.updateWorldMatrix(true, true)
    const helperBounds = new THREE.Box3().setFromObject(helperRoot)
    if (!helperBounds.isEmpty()) {
      box.union(helperBounds)
    }
  }

  return box
}

function getExtraModelDefinition(slot: ExtraModelSlot): ExtraModelSlotDefinition {
  const definition = EXTRA_MODEL_SLOTS.find((item) => item.slot === slot)
  if (!definition) throw new Error(`未知的附加模型槽位: ${slot}`)
  return definition
}

/** 重复加载同一资产并分配独立图层，用于验证多实例与资源缓存复用/隔离。 */
async function addExtraModel(slot: ExtraModelSlot): Promise<void> {
  const engineInstance = engine.value
  const plugin = getPointCloudPlugin()
  if (!engineInstance || !plugin || extraModels[slot]) {
    return
  }

  const definition = getExtraModelDefinition(slot)
  extraModelError.value = ''
  try {
    const handle = await engineInstance.loadPointCloud({
      url: resolvePointCloudInputPath(DEFAULT_POINT_CLOUD_MODEL),
      name: `Multi View Point Cloud ${slot}`,
    })
    const object3D = handle.object3D
    if (object3D instanceof THREE.Object3D) {
      object3D.position.add(new THREE.Vector3(...definition.offset))
    }
    // setLayerMask 同时触发渲染失效，因此放在位置调整之后。
    plugin.setLayerMask(handle.id, definition.layer)
    extraModels[slot] = { assetId: handle.id, visible: true }
    updatePointCloudAssetCount()
  } catch (error) {
    extraModelError.value = error instanceof Error ? error.message : `模型 ${slot} 加载失败`
  }
}

function removeExtraModel(slot: ExtraModelSlot): void {
  const plugin = getPointCloudPlugin()
  const state = extraModels[slot]
  if (!plugin || !state) {
    return
  }
  plugin.unmount(state.assetId)
  plugin.dispose(state.assetId)
  extraModels[slot] = null
  extraModelError.value = ''
  updatePointCloudAssetCount()
}

function toggleExtraModelVisible(slot: ExtraModelSlot): void {
  const plugin = getPointCloudPlugin()
  const state = extraModels[slot]
  if (!plugin || !state) {
    return
  }
  state.visible = !state.visible
  plugin.setVisibility(state.assetId, state.visible)
}

function clearExtraModels(): void {
  for (const definition of EXTRA_MODEL_SLOTS) {
    removeExtraModel(definition.slot)
  }
}

function updatePointCloudAssetCount() {
  pointCloudAssetCount.value = getPointCloudPlugin()?.getAll().length ?? 0
}

/**
 * 附加 3DGS / 3D Tiles 实例：用于多视口回归覆盖点云之外的资产类型
 * （加载 → 显隐 → 卸载，且与点云复用同一套 layer mask 分视口约定）。
 */
type ExtraAssetKind = 'gsplat' | 'tiles'

interface ExtraAssetState {
  readonly id: string
  readonly layer: number
  visible: boolean
}

const extraAssets = reactive<Record<ExtraAssetKind, ExtraAssetState | null>>({
  gsplat: null,
  tiles: null,
})
const extraAssetError = ref('')
const extraAssetLoading = reactive<Record<ExtraAssetKind, boolean>>({
  gsplat: false,
  tiles: false,
})

function readExtraAssetUrlParam(key: string): string | null {
  if (typeof window === 'undefined') return null
  const query = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  const value = query.get(key)
  return value && value.trim() ? value.trim() : null
}

const EXTRA_ASSET_URL_OVERRIDES = {
  gsplat: readExtraAssetUrlParam('gsplatUrl'),
  tiles: readExtraAssetUrlParam('tilesUrl'),
}

const EXTRA_ASSET_LABELS: Record<ExtraAssetKind, { label: string; layer: number; accent: string; hint: string }> = {
  gsplat: {
    label: '3DGS 实例',
    layer: INSPECT_LAYER_MASK,
    accent: '#ffb36a',
    hint: '3DGS 只在 Inspect View 可见，用于验证多视口下的资源加载与释放。',
  },
  tiles: {
    label: '3D Tiles 实例',
    layer: TOP_LAYER_MASK,
    accent: '#73f4d1',
    hint: '3D Tiles 只在俯视图可见，用于验证流式资产在多视口下的加载与释放。',
  },
}

function applyExtraAssetLayer(object: THREE.Object3D | null | undefined, layer: number): void {
  object?.traverse((node) => node.layers.set(Math.log2(layer)))
}

function getGsplatService(): {
  dispose(id: string): void
  getAll(): unknown[]
  setVisibility(id: string, visible: boolean): void
} | null {
  const engineInstance = engine.value
  if (!engineInstance) return null
  return engineInstance.context.getService('GaussianSplatService') as unknown as {
    dispose(id: string): void
    getAll(): unknown[]
    setVisibility(id: string, visible: boolean): void
  }
}

async function addExtraAsset(kind: ExtraAssetKind): Promise<void> {
  const engineInstance = engine.value
  if (!engineInstance || extraAssets[kind] || extraAssetLoading[kind]) {
    return
  }
  const definition = EXTRA_ASSET_LABELS[kind]
  extraAssetError.value = ''
  extraAssetLoading[kind] = true
  try {
    if (kind === 'gsplat') {
      const handle = await engineInstance.loadGaussianSplat({
        url: EXTRA_ASSET_URL_OVERRIDES.gsplat ?? resolvePresetModelUrl(PRESET_MODELS[0].path),
        name: 'Multi View 3DGS',
      })
      applyExtraAssetLayer(handle.object3D, definition.layer)
      extraAssets.gsplat = { id: handle.id, layer: definition.layer, visible: true }
    } else {
      const fixture = TILES_FIXTURES.delaunay_image
      const overrideUrl = EXTRA_ASSET_URL_OVERRIDES.tiles
      const tileset = await engineInstance.tiles.load({
        id: `multi-view-tiles-${Date.now()}`,
        name: 'Multi View 3D Tiles',
        url: overrideUrl ?? fixture.url,
        coordinate: { mode: 'local', upAxis: 'Z', unit: 'meter' },
        sourceBounds: overrideUrl ? undefined : fixture.sourceBounds,
        transform: new THREE.Matrix4(),
        options: { maxScreenSpaceError: 12 },
      })
      engineInstance.tiles.mount(tileset)
      extraAssets.tiles = { id: tileset.id, layer: definition.layer, visible: true }
    }
  } catch (error) {
    extraAssetError.value = error instanceof Error ? error.message : `附加 ${definition.label} 加载失败`
  } finally {
    extraAssetLoading[kind] = false
  }
}

function removeExtraAsset(kind: ExtraAssetKind): void {
  const engineInstance = engine.value
  const state = extraAssets[kind]
  if (!engineInstance || !state) return
  if (kind === 'gsplat') {
    getGsplatService()?.dispose(state.id)
  } else {
    engineInstance.tiles.unmount(state.id)
    engineInstance.tiles.dispose(state.id)
  }
  extraAssets[kind] = null
  extraAssetError.value = ''
}

function toggleExtraAssetVisible(kind: ExtraAssetKind): void {
  const engineInstance = engine.value
  const state = extraAssets[kind]
  if (!engineInstance || !state) return
  state.visible = !state.visible
  if (kind === 'gsplat') {
    getGsplatService()?.setVisibility(state.id, state.visible)
  } else {
    engineInstance.tiles.setVisibility(state.id, state.visible)
  }
}

function clearExtraAssets(): void {
  removeExtraAsset('gsplat')
  removeExtraAsset('tiles')
}

function extraAssetCount(kind: ExtraAssetKind): 0 | 1 {
  return extraAssets[kind] ? 1 : 0
}

// 供 9.3 多视口压力脚本驱动的内部探针（与 usePotreeEngine 的 __EXAMPLE_ENGINE__ 同源约定）。
Reflect.set(window, '__MULTIVIEW_EXTRA__', {
  snapshot: () => ({
    gsplat: extraAssets.gsplat ? { ...extraAssets.gsplat } : null,
    tiles: extraAssets.tiles ? { ...extraAssets.tiles } : null,
    gsplatCount: getGsplatService()?.getAll?.().length ?? 0,
    tilesCount: engine.value?.tiles?.getAll?.().length ?? 0,
  }),
  add: (kind: ExtraAssetKind) => addExtraAsset(kind),
  remove: (kind: ExtraAssetKind) => removeExtraAsset(kind),
  toggle: (kind: ExtraAssetKind) => toggleExtraAssetVisible(kind),
})

function ensureViewSpaceMouseDefaultProfile(viewId: ManagedViewId) {
  if (appliedSpaceMouseDefaults.has(viewId)) {
    return
  }

  const controller = getViewSpaceMouseController(viewId)
  if (!controller) {
    return
  }

  applySpaceMouseNavigationProfile(
    controller,
    getViewDefinition(viewId).defaultSpaceMouseNavigationProfile,
  )
  appliedSpaceMouseDefaults.add(viewId)
}

function syncViewState(viewId: ManagedViewId) {
  const rig = getViewRig(viewId)
  const manager = getViewControlManager(viewId)
  const snapshot = getViewHandle(viewId)?.getSnapshot()

  if (rig) {
    viewStates[viewId].cameraMode = rig.mode
  }

  if (manager) {
    viewStates[viewId].controlMode = manager.activeMode
  }

  if (snapshot) {
    viewStates[viewId].layerMask = snapshot.layerMask
  }

  viewStates[viewId].spaceMouseNavigation = readSpaceMouseNavigationState(
    getViewSpaceMouseController(viewId),
    getViewDefinition(viewId).defaultSpaceMouseNavigationProfile,
  )
}

function syncAllViewState() {
  for (const definition of VIEW_DEFINITIONS) {
    syncViewState(definition.id)
  }

  activeViewId.value = toManagedViewId(engine.value?.views.getActive()?.id)
  updatePointCloudAssetCount()
}

function ensureManagedViews() {
  const engineInstance = engine.value

  if (!engineInstance) {
    return
  }

  const primary = engineInstance.views.get('primary')
  primary?.setLayerMask(BASE_LAYER_MASK)
  primary?.setUserData({ role: 'main' })

  const inspect =
    engineInstance.views.get('inspect') ??
    engineInstance.views.create({
      id: 'inspect',
      rect: { x: 0, y: 0, width: 1, height: 1 },
      zIndex: 1,
      layerMask: BASE_LAYER_MASK | INSPECT_LAYER_MASK,
      userData: { role: 'inspect' },
    })
  inspect.setLayerMask(BASE_LAYER_MASK | INSPECT_LAYER_MASK)
  inspect.setUserData({ role: 'inspect' })

  const top =
    engineInstance.views.get('top') ??
    engineInstance.views.create({
      id: 'top',
      rect: { x: 0, y: 0, width: 1, height: 1 },
      zIndex: 2,
      layerMask: BASE_LAYER_MASK | TOP_LAYER_MASK,
      userData: { role: 'top' },
    })
  top.setLayerMask(BASE_LAYER_MASK | TOP_LAYER_MASK)
  top.setUserData({ role: 'top' })

  engineInstance.views.setActive('primary')
}

function applyDefaultViewConfiguration() {
  for (const definition of VIEW_DEFINITIONS) {
    const rig = getViewRig(definition.id)
    const manager = getViewControlManager(definition.id)
    const handle = getViewHandle(definition.id)

    handle?.setLayerMask(definition.layerMask)
    rig?.setMode(definition.defaultCameraMode)
    rig?.setPresetView(definition.defaultPreset)
    manager?.setMode(definition.defaultControlMode)
    ensureViewSpaceMouseDefaultProfile(definition.id)
  }

  syncAllViewState()
  fitAllViews()
}

function focusView(viewId: ManagedViewId) {
  engine.value?.views.setActive(viewId)
  containerRef.value?.focus()
  syncAllViewState()
}

function fitView(viewId: ManagedViewId) {
  const box = getSceneFitBox()
  if (box.isEmpty()) {
    return
  }

  getViewRig(viewId)?.fitToBox(box)
  syncViewState(viewId)
}

function fitAllViews() {
  for (const definition of VIEW_DEFINITIONS) {
    fitView(definition.id)
  }
}

function setViewPreset(viewId: ManagedViewId, preset: ViewPreset) {
  const rig = getViewRig(viewId)
  if (!rig) {
    return
  }

  rig.setPresetView(preset)
  fitView(viewId)
}

function toggleViewCameraMode(viewId: ManagedViewId) {
  const rig = getViewRig(viewId)
  if (!rig) {
    return
  }

  const nextMode =
    rig.mode === CameraMode.Perspective ? CameraMode.Orthographic : CameraMode.Perspective

  if (
    nextMode === CameraMode.Orthographic &&
    requiresPerspectiveControlMode(viewStates[viewId].controlMode)
  ) {
    getViewControlManager(viewId)?.setMode('orbit')
  }

  rig.setMode(nextMode)
  syncViewState(viewId)
}

function setViewControlMode(viewId: ManagedViewId, mode: ControlMode) {
  const rig = getViewRig(viewId)
  const manager = getViewControlManager(viewId)
  if (!rig || !manager) {
    return
  }

  if (requiresPerspectiveControlMode(mode) && rig.mode !== CameraMode.Perspective) {
    rig.setMode(CameraMode.Perspective)
  }

  manager.setMode(mode)
  syncViewState(viewId)
}

function handleViewPresetSelection(viewId: ManagedViewId, value: string) {
  setViewPreset(viewId, value as ViewPreset)
}

function handleViewControlModeSelection(viewId: ManagedViewId, value: string) {
  setViewControlMode(viewId, value as ControlMode)
}

function handleViewSpaceMouseSecondaryControlGroupSelection(
  viewId: ManagedViewId,
  payload: {
    groupId: string
    value: string
  },
) {
  applySpaceMouseSecondaryControlSelection(
    getViewSpaceMouseController(viewId),
    payload.groupId,
    payload.value,
  )
  syncViewState(viewId)
}

function teardownBindings() {
  while (cleanupTasks.length > 0) {
    const task = cleanupTasks.pop()
    task?.()
  }
}

function bindEngineEvents() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  teardownBindings()

  const handleViewActivated = (event: { viewId: string }) => {
    activeViewId.value = toManagedViewId(event.viewId)
    syncAllViewState()
  }
  const handleViewCameraModeChanged = (event: { viewId: string }) => {
    const viewId = toManagedViewId(event.viewId)
    syncViewState(viewId)
  }
  const handleViewControlsModeChanged = (event: { viewId: string }) => {
    const viewId = toManagedViewId(event.viewId)
    syncViewState(viewId)
  }
  const handleViewUpdated = (event: { viewId: string }) => {
    const viewId = toManagedViewId(event.viewId)
    syncViewState(viewId)
  }
  const handleSpaceMouseNavigationChanged = (event: { viewId: string | null }) => {
    if (event.viewId === null) {
      syncAllViewState()
      return
    }

    syncViewState(toManagedViewId(event.viewId))
  }

  engineInstance.events.on('view.activated', handleViewActivated)
  engineInstance.events.on('view.camera.modeChanged', handleViewCameraModeChanged)
  engineInstance.events.on('view.controls.modeChanged', handleViewControlsModeChanged)
  engineInstance.events.on('view.updated', handleViewUpdated)
  engineInstance.events.on('controls.spacemouseNavigationChanged', handleSpaceMouseNavigationChanged)

  cleanupTasks.push(() => engineInstance.events.off('view.activated', handleViewActivated))
  cleanupTasks.push(() =>
    engineInstance.events.off('view.camera.modeChanged', handleViewCameraModeChanged),
  )
  cleanupTasks.push(() =>
    engineInstance.events.off('view.controls.modeChanged', handleViewControlsModeChanged),
  )
  cleanupTasks.push(() => engineInstance.events.off('view.updated', handleViewUpdated))
  cleanupTasks.push(() =>
    engineInstance.events.off('controls.spacemouseNavigationChanged', handleSpaceMouseNavigationChanged),
  )

  const clipSubscription = getClipService()?.onChanged(() => {
    syncPrimaryClipState()
  })
  if (clipSubscription) {
    cleanupTasks.push(() => clipSubscription.dispose())
  }

  syncAllViewState()
  syncPrimaryClipState()
}

function measureViewRect(viewId: ManagedViewId): ViewRect | null {
  const stage = stageShellRef.value
  const element = viewElements.get(viewId)
  if (!stage || !element) {
    return null
  }

  const stageRect = stage.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const rect = roundRect({
    x: elementRect.left - stageRect.left,
    y: elementRect.top - stageRect.top,
    width: elementRect.width,
    height: elementRect.height,
  })

  if (rect.width <= 0 || rect.height <= 0) {
    return null
  }

  return rect
}

function syncViewRects() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  for (const definition of VIEW_DEFINITIONS) {
    const rect = measureViewRect(definition.id)
    if (!rect) {
      continue
    }

    engineInstance.views.setRect(definition.id, rect)
  }

  engineInstance.resize()
}

function scheduleViewRectSync() {
  if (resizeRaf !== 0) {
    return
  }

  resizeRaf = window.requestAnimationFrame(() => {
    resizeRaf = 0
    syncViewRects()
  })
}

function setupResizeObserver() {
  if (!stageShellRef.value) {
    return
  }

  resizeObserver?.disconnect()
  resizeObserver = new ResizeObserver(() => {
    scheduleViewRectSync()
  })
  resizeObserver.observe(stageShellRef.value)
}

function setupViewportSyncListeners() {
  detachViewportSyncListeners?.()

  const handleViewportMutation = () => {
    scheduleViewRectSync()
  }

  window.addEventListener('resize', handleViewportMutation)
  window.visualViewport?.addEventListener('resize', handleViewportMutation)

  let detachPixelRatioListener: (() => void) | null = null
  if (typeof window.matchMedia === 'function') {
    const bindPixelRatioListener = () => {
      detachPixelRatioListener?.()
      const query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
      const handleChange = () => {
        scheduleViewRectSync()
        bindPixelRatioListener()
      }
      query.addEventListener('change', handleChange)
      detachPixelRatioListener = () => {
        query.removeEventListener('change', handleChange)
      }
    }

    bindPixelRatioListener()
  }

  detachViewportSyncListeners = () => {
    window.removeEventListener('resize', handleViewportMutation)
    window.visualViewport?.removeEventListener('resize', handleViewportMutation)
    detachPixelRatioListener?.()
    detachPixelRatioListener = null
  }
}

function clearPointCloudScene() {
  const pointCloudPlugin = getPointCloudPlugin()

  for (const handle of pointCloudPlugin?.getAll() ?? []) {
    pointCloudPlugin?.unmount(handle.id)
    pointCloudPlugin?.dispose(handle.id)
  }

  clearPrimaryScopedClipVolume()
  pointCloudLoadError.value = ''
  extraModels.B = null
  extraModels.C = null
  extraModelError.value = ''
  updatePointCloudAssetCount()
  fitAllViews()
}

async function loadPointCloud(inputPath?: string) {
  const engineInstance = engine.value

  if (!engineInstance || pointCloudLoading.value) {
    return
  }

  const rawPath = (inputPath ?? pointCloudInput.value).trim()
  if (!rawPath) {
    pointCloudLoadError.value = '请先提供 metadata.json 路径或选择本地目录。'
    return
  }

  pointCloudInput.value = rawPath
  pointCloudLoadError.value = ''
  pointCloudLoading.value = true

  try {
    clearPointCloudScene()

    const handle = await engineInstance.loadPointCloud({
      url: resolvePointCloudInputPath(rawPath),
      name: 'Multi View Point Cloud',
    })

    updatePointCloudAssetCount()
    const fitBox = engineInstance.context.sceneBounds.getWorldBoundingBox({
      ids: [handle.id],
      includeInvisible: true,
      purpose: 'fit',
    })
    applyPrimaryScopedClipVolume(fitBox.isEmpty() ? null : fitBox)
    if (!fitBox.isEmpty()) {
      for (const definition of VIEW_DEFINITIONS) {
        getViewRig(definition.id)?.fitToBox(fitBox)
      }
    } else {
      fitAllViews()
    }
    syncAllViewState()
  } catch (error) {
    pointCloudLoadError.value = error instanceof Error ? error.message : '未知错误'
  } finally {
    pointCloudLoading.value = false
  }
}

onMounted(async () => {
  await graphicsCard.runPreflight()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(actualBackend.value)

  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  createHelperScene(engineInstance)
  ensureManagedViews()
  bindEngineEvents()
  applyDefaultViewConfiguration()
  await nextTick()
  setupResizeObserver()
  setupViewportSyncListeners()
  scheduleViewRectSync()
  await loadPointCloud()
})

onBeforeUnmount(() => {
  teardownBindings()
  detachViewportSyncListeners?.()
  detachViewportSyncListeners = null
  resizeObserver?.disconnect()
  resizeObserver = null
  if (resizeRaf !== 0) {
    window.cancelAnimationFrame(resizeRaf)
    resizeRaf = 0
  }
  clearHelperRoot()
  helperRoot = null
})
</script>

<template>
  <div
    class="multi-viewport-page"
    :class="{ 'embedded-frame': isEmbeddedFrame, 'panel-collapsed': isPanelCollapsed }"
  >
    <section class="stage-column">
      <header v-if="!isEmbeddedFrame" class="stage-header">
        <div class="stage-copy">
          <p class="stage-kicker">Multi Viewport Example</p>
          <h1 class="stage-title">单 Engine，多 View，同场景分视图演示</h1>
          <p class="stage-description">
            点击任一视口即可切换 active view。三个视口共享同一个 engine 和同一份场景，但相机、控制器和 layer
            mask 独立，能直接观察主视图、inspect 视图和俯视图的差异。当前示例还额外放置了一个仅对主视图生效的
            scoped 裁切盒，用来演示同场景下按 view 隔离的 clip volume。
          </p>
        </div>

        <div class="stage-pill-list">
          <span class="stage-pill">主视图：基础层 + Scoped Clip</span>
          <span class="stage-pill stage-pill-inspect">Inspect：橙色巡检层</span>
          <span class="stage-pill stage-pill-top">Top：青色俯视层</span>
        </div>
      </header>

      <div ref="stageShellRef" class="stage-shell">
        <div ref="containerRef" class="canvas-host"></div>

        <div class="viewport-layout">
          <div
            v-for="definition in VIEW_DEFINITIONS"
            :key="definition.id"
            :ref="(element) => setViewElement(definition.id, element)"
            class="viewport-pane"
            :class="`viewport-pane-${definition.id}`"
            :style="{ '--view-accent': definition.accent }"
          >
            <div class="viewport-frame" :class="{ active: activeViewId === definition.id }">
              <button
                class="viewport-tag"
                type="button"
                @pointerdown.prevent
                @click="focusView(definition.id)"
              >
                <span class="viewport-tag-title">{{ definition.title }}</span>
                <span class="viewport-tag-meta">{{ getViewportTagMeta(definition.id) }}</span>
              </button>

              <span class="viewport-hint">{{ definition.overlayHint }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <aside class="panel-column" :class="{ 'is-collapsed': isPanelCollapsed }">
      <button
        v-if="isPanelCollapsed"
        class="panel-expand-button"
        type="button"
        aria-label="展开控制中心"
        :aria-expanded="false"
        @click="isPanelCollapsed = false"
      >
        <span>控制中心</span>
        <span aria-hidden="true">☰</span>
      </button>

      <div v-show="!isPanelCollapsed" class="panel-shell">
        <div class="panel-shell-summary">
          <span class="summary-main">
            <span class="panel-kicker">右侧面板</span>
            <span class="panel-title">多视口控制中心</span>
          </span>
          <span class="summary-side">
            <span class="panel-shell-meta">{{ activeViewDefinition.title }}</span>
            <button
              class="panel-collapse-button"
              type="button"
              aria-label="收起控制中心"
              :aria-expanded="true"
              @click="isPanelCollapsed = true"
            >
              ›
            </button>
          </span>
        </div>

        <div class="panel-shell-body">
          <SceneAssetLoaderCard
            v-model="pointCloudInput"
            title="点云场景"
            subtitle="可直接加载默认 metadata.json，也可切换到本地目录。"
            badge="共享场景"
            :hint="pointCloudLoaderHint"
            :loading="pointCloudLoading"
            :can-clear="pointCloudAssetCount > 0"
            clear-label="清空点云"
            load-label="加载点云"
            directory-pick-label="选择本地目录"
            :directory-entry-names="['metadata.json']"
            @submit-load="loadPointCloud()"
            @clear-scene="clearPointCloudScene()"
          />

          <details class="panel-section" open>
            <summary class="panel-section-summary">
              <span class="summary-main">
                <span class="panel-section-title">多模型 A / B / C</span>
                <span class="panel-section-subtitle">同一资产多实例按图层分配到不同视口</span>
              </span>
              <span class="summary-side">
                <span class="summary-count">{{ extraModelCountLabel }}</span>
                <span class="summary-chevron" aria-hidden="true">›</span>
              </span>
            </summary>

            <div class="panel-section-body">
              <p class="panel-hint">
                模型 A 是当前共享点云（图层 L0）；B 走 L1，只在 Inspect View 可见；C 走 L2，只在俯视图可见。
              </p>

              <div
                v-for="definition in EXTRA_MODEL_SLOTS"
                :key="definition.slot"
                class="extra-model-row"
                :style="{ '--extra-accent': definition.accent }"
              >
                <span class="extra-model-name">{{ definition.label }}</span>
                <div class="button-grid">
                  <button
                    v-if="!extraModels[definition.slot]"
                    class="panel-button"
                    type="button"
                    :disabled="pointCloudLoading"
                    @click="addExtraModel(definition.slot)"
                  >
                    添加实例
                  </button>
                  <template v-else>
                    <button
                      class="panel-button"
                      :class="{ active: extraModels[definition.slot]?.visible }"
                      type="button"
                      @click="toggleExtraModelVisible(definition.slot)"
                    >
                      {{ extraModels[definition.slot]?.visible ? '隐藏' : '显示' }}
                    </button>
                    <button
                      class="panel-button panel-button-subtle"
                      type="button"
                      @click="removeExtraModel(definition.slot)"
                    >
                      移除
                    </button>
                  </template>
                </div>
              </div>

              <button
                class="panel-button panel-button-wide panel-button-subtle"
                type="button"
                :disabled="extraModels.B === null && extraModels.C === null"
                @click="clearExtraModels()"
              >
                移除全部附加实例
              </button>

              <p class="panel-hint">
                {{ extraModelError || '附加实例复用同一份点云缓存，卸载其中一个不应影响其它实例。' }}
              </p>
            </div>
          </details>

          <details class="panel-section" open>
            <summary class="panel-section-summary">
              <span class="summary-main">
                <span class="panel-section-title">附加资产：3DGS / 3D Tiles</span>
                <span class="panel-section-subtitle">非点云资产的多视口加载、显隐与卸载回归</span>
              </span>
              <span class="summary-side">
                <span class="summary-count">{{ extraAssetCount('gsplat') + extraAssetCount('tiles') }} / 2</span>
                <span class="summary-chevron" aria-hidden="true">›</span>
              </span>
            </summary>

            <div class="panel-section-body">
              <p class="panel-hint">
                3DGS 走 Inspect 图层（橙色），3D Tiles 走俯视图层（青色）；用于验证加载、显隐、卸载在多视口下都能完整释放。
              </p>

              <div
                v-for="kind in (['gsplat', 'tiles'] as const)"
                :key="kind"
                class="extra-model-row"
                :data-extra-asset="kind"
                :style="{ '--extra-accent': EXTRA_ASSET_LABELS[kind].accent }"
              >
                <span class="extra-model-name">{{ EXTRA_ASSET_LABELS[kind].label }}</span>
                <div class="button-grid">
                  <button
                    v-if="!extraAssets[kind]"
                    class="panel-button"
                    type="button"
                    :disabled="extraAssetLoading[kind]"
                    @click="addExtraAsset(kind)"
                  >
                    添加实例
                  </button>
                  <template v-else>
                    <button
                      class="panel-button"
                      :class="{ active: extraAssets[kind]?.visible }"
                      type="button"
                      @click="toggleExtraAssetVisible(kind)"
                    >
                      {{ extraAssets[kind]?.visible ? '隐藏' : '显示' }}
                    </button>
                    <button
                      class="panel-button panel-button-subtle"
                      type="button"
                      @click="removeExtraAsset(kind)"
                    >
                      移除
                    </button>
                  </template>
                </div>
              </div>

              <button
                class="panel-button panel-button-wide panel-button-subtle"
                type="button"
                :disabled="!extraAssets.gsplat && !extraAssets.tiles"
                @click="clearExtraAssets()"
              >
                移除全部附加资产
              </button>

              <p class="panel-hint">
                {{ extraAssetError || EXTRA_ASSET_LABELS.gsplat.hint }}
              </p>
            </div>
          </details>

          <GraphicsBackendControlCard :card="graphicsCard" />

          <SceneStatusCard
            title="视图总览"
            subtitle="这里汇总当前 active view、共享场景和多视口布局状态。"
            :badge="activeViewDefinition.title"
            :items="statusItems"
            hint="橙色巡检路径只在 Inspect View 可见，青色柱状标记只在俯视图可见；主视图还会额外应用一个 scoped box clip，其他视图保持完整点云。"
            action-label="适配全部视图"
            action-variant="neutral"
            @action="fitAllViews()"
          />

          <details class="panel-section" open>
            <summary class="panel-section-summary">
              <span class="summary-main">
                <span class="panel-section-title">图层与布局约定</span>
                <span class="panel-section-subtitle">同一份场景在不同 view 中使用不同 layer mask 和 scoped clip volume 过滤。</span>
              </span>
              <span class="summary-side">
                <span class="summary-count">L0 / L1 / L2</span>
                <span class="summary-chevron" aria-hidden="true">›</span>
              </span>
            </summary>

            <div class="panel-section-body">
              <div class="legend-list">
                <div
                  v-for="definition in VIEW_DEFINITIONS"
                  :key="`${definition.id}-legend`"
                  class="legend-item"
                >
                  <span
                    class="legend-swatch"
                    :style="{ background: definition.accent }"
                  ></span>
                  <div class="legend-copy">
                    <strong class="legend-title">{{ definition.title }}</strong>
                    <p class="legend-text">
                      可见层：{{ formatLayerMask(viewStates[definition.id].layerMask) }}。默认控制器：{{
                        formatControlModeLabel(viewStates[definition.id].controlMode)
                      }}。{{
                        definition.id === 'primary'
                          ? '额外启用 scoped 裁切盒，inspect / top 不受影响。'
                          : '不应用主视图的 scoped 裁切盒。'
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <SceneViewCard
            v-for="definition in VIEW_DEFINITIONS"
            :key="`${definition.id}-card`"
            :title="definition.title"
            :subtitle="definition.subtitle"
            :badge="getViewCardBadge(definition.id)"
            :is-perspective="viewStates[definition.id].cameraMode === CameraMode.Perspective"
            :options="VIEW_OPTIONS"
            :control-mode-options="VIEW_CONTROL_MODE_OPTIONS"
            :active-control-mode="viewStates[definition.id].controlMode"
            :secondary-control-groups="
              viewStates[definition.id].controlMode === 'spacemouse'
                ? buildSpaceMouseSecondaryControlGroups(viewStates[definition.id].spaceMouseNavigation)
                : []
            "
            control-title="控制模式"
            control-subtitle="每个视图记住自己的控制器配置，切回 active 时会自动恢复。"
            :mode-toggle-label="
              viewStates[definition.id].cameraMode === CameraMode.Perspective
                ? '切换正交'
                : '切换透视'
            "
            fit-label="适配该视图"
            @toggle-mode="toggleViewCameraMode(definition.id)"
            @fit="fitView(definition.id)"
            @set-view="handleViewPresetSelection(definition.id, $event)"
            @set-control-mode="handleViewControlModeSelection(definition.id, $event)"
            @set-secondary-control-group-mode="
              handleViewSpaceMouseSecondaryControlGroupSelection(definition.id, $event)
            "
          />
        </div>
      </div>
    </aside>
  </div>
</template>

<style scoped>
.multi-viewport-page {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 392px;
  height: 100dvh;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(68, 113, 188, 0.18), transparent 30%),
    radial-gradient(circle at bottom right, rgba(20, 124, 108, 0.18), transparent 28%),
    #08111a;
  color: #edf5fb;
}

.stage-column {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
  padding: 16px 0 16px 16px;
}

.stage-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 0 6px 0 2px;
}

.stage-copy {
  min-width: 0;
}

.stage-kicker {
  margin: 0;
  color: #8fd9ff;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.stage-title {
  margin: 10px 0 0;
  font-size: clamp(26px, 3vw, 38px);
  line-height: 1.05;
}

.stage-description {
  max-width: 780px;
  margin: 10px 0 0;
  color: rgba(228, 238, 247, 0.72);
  font-size: 13px;
  line-height: 1.7;
}

.stage-pill-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.stage-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 5px 10px;
  border: 1px solid rgba(123, 217, 255, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #dff4ff;
  font-size: 11px;
  white-space: nowrap;
}

.stage-pill-inspect {
  border-color: rgba(255, 179, 106, 0.3);
  color: #ffd8b0;
}

.stage-pill-top {
  border-color: rgba(115, 244, 209, 0.3);
  color: #c3fff1;
}

.stage-shell {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  background:
    radial-gradient(circle at top, rgba(78, 123, 209, 0.12), transparent 40%),
    linear-gradient(180deg, rgba(7, 12, 20, 0.96), rgba(3, 6, 11, 0.98));
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.canvas-host {
  position: absolute;
  inset: 0;

  :deep(canvas) {
    outline: none;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
}

.viewport-layout {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(250px, 0.92fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 14px;
  pointer-events: none;
}

.viewport-pane-primary {
  grid-row: 1 / span 2;
}

.viewport-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0)),
    rgba(255, 255, 255, 0.01);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.03),
    inset 0 0 46px rgba(255, 255, 255, 0.03);
}

.viewport-frame::after {
  position: absolute;
  inset: 0;
  content: '';
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.viewport-frame.active {
  border-color: color-mix(in srgb, var(--view-accent) 70%, white 10%);
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--view-accent) 24%, transparent),
    0 0 0 1px color-mix(in srgb, var(--view-accent) 18%, transparent);
}

.viewport-tag,
.viewport-hint {
  position: absolute;
  z-index: 2;
}

.viewport-tag {
  top: 12px;
  right: 12px;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  max-width: calc(100% - 24px);
  padding: 10px 12px;
  border: 1px solid color-mix(in srgb, var(--view-accent) 28%, white 8%);
  border-radius: 14px;
  background: rgba(6, 11, 18, 0.72);
  color: #eff7fd;
  text-align: right;
  cursor: pointer;
  pointer-events: auto;
  backdrop-filter: blur(12px);
}

.viewport-tag-title {
  font-size: 13px;
  font-weight: 700;
}

.viewport-tag-meta {
  color: rgba(231, 239, 247, 0.72);
  font-size: 11px;
  line-height: 1.5;
}

.viewport-hint {
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  max-width: calc(100% - 24px);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(6, 11, 18, 0.66);
  color: color-mix(in srgb, var(--view-accent) 72%, white 20%);
  font-size: 11px;
  text-align: center;
}

.panel-column {
  min-width: 0;
  min-height: 0;
  padding: 16px;
  overflow: hidden;
}

.panel-column.is-collapsed {
  padding: 0;
  overflow: visible;
}

.multi-viewport-page.panel-collapsed {
  grid-template-columns: minmax(0, 1fr) 0;
}

.panel-expand-button {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1001;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  border: 1px solid rgba(132, 207, 244, 0.16);
  border-radius: 11px;
  background: rgba(9, 16, 24, 0.72);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
  color: rgba(205, 237, 250, 0.78);
  cursor: pointer;
  font-size: 10px;
  backdrop-filter: blur(12px);
}

.panel-expand-button:hover {
  border-color: rgba(132, 207, 244, 0.32);
  background: rgba(15, 29, 41, 0.84);
  color: #d9f5ff;
}

.panel-collapse-button {
  display: inline-grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(136, 210, 247, 0.14);
  border-radius: 9px;
  background: rgba(75, 157, 204, 0.07);
  color: rgba(185, 229, 249, 0.72);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.panel-collapse-button:hover {
  border-color: rgba(136, 210, 247, 0.3);
  background: rgba(75, 157, 204, 0.14);
  color: #d6f3ff;
}

.panel-shell {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(160, 196, 220, 0.18);
  background:
    linear-gradient(180deg, rgba(14, 20, 29, 0.96) 0%, rgba(8, 11, 17, 0.94) 100%);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.24);
  color: #f3f7fb;
}

.panel-shell-summary {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px;
  background:
    linear-gradient(180deg, rgba(14, 21, 31, 0.96) 0%, rgba(14, 21, 31, 0.9) 72%, rgba(14, 21, 31, 0.82) 100%);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.12);
}

.panel-shell-body {
  min-height: 0;
  padding: 12px 10px 20px 16px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-bottom: 20px;
  scrollbar-width: thin;
  scrollbar-color: rgba(153, 187, 208, 0.3) transparent;
}

.summary-main,
.summary-side {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.summary-main {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.summary-side {
  flex-shrink: 0;
}

.panel-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #82b8d6;
}

.panel-title {
  font-size: 23px;
  font-weight: 700;
  line-height: 1.05;
  color: #f7fbff;
}

.panel-shell-meta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d8e9f4;
}

.legend-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
}

.legend-swatch {
  display: inline-flex;
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border-radius: 999px;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.06);
}

.legend-copy {
  min-width: 0;
}

.legend-title {
  display: block;
  font-size: 13px;
  color: #f5f9fd;
}

.legend-text {
  margin: 4px 0 0;
  color: #9fb7c8;
  font-size: 12px;
  line-height: 1.5;
}

.panel-shell :deep(.panel-section),
.panel-shell :deep(.panel-subsection) {
  flex: 0 0 auto;
  margin: 0;
  overflow: hidden;
  list-style: none;
}

.panel-shell :deep(.panel-section:not([open]) .panel-section-summary),
.panel-shell :deep(.panel-subsection:not([open]) .panel-subsection-summary) {
  align-items: center;
}

.panel-shell :deep(.panel-section:not([open]) .summary-main),
.panel-shell :deep(.panel-subsection:not([open]) .summary-main) {
  gap: 0;
}

.panel-shell :deep(.panel-section:not([open]) .panel-section-subtitle),
.panel-shell :deep(.panel-subsection:not([open]) .panel-subsection-subtitle) {
  display: none;
}

.panel-shell :deep(.panel-section) {
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.panel-shell :deep(.panel-subsection) {
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.14);
}

.panel-shell :deep(.panel-section-summary),
.panel-shell :deep(.panel-subsection-summary) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  list-style: none;
}

.panel-shell :deep(.panel-section-summary) {
  padding: 14px 14px 12px;
}

.panel-shell :deep(.panel-subsection-summary) {
  padding: 12px 12px 10px;
}

.panel-shell :deep(.panel-section-summary::-webkit-details-marker),
.panel-shell :deep(.panel-subsection-summary::-webkit-details-marker) {
  display: none;
}

.panel-shell :deep(.panel-section-body),
.panel-shell :deep(.panel-subsection-body) {
  padding: 0 14px 14px;
}

.panel-shell :deep(.summary-main),
.panel-shell :deep(.summary-side) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.panel-shell :deep(.summary-main) {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.panel-shell :deep(.summary-side) {
  flex-shrink: 0;
}

.panel-shell :deep(.summary-chevron) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 18px;
  line-height: 1;
  color: #98b8cb;
  transform: rotate(0deg);
  transition: transform 140ms ease;
}

.panel-shell :deep(.panel-section[open] > .panel-section-summary .summary-chevron),
.panel-shell :deep(.panel-subsection[open] > .panel-subsection-summary .summary-chevron) {
  transform: rotate(90deg);
}

.panel-shell :deep(.panel-section-title),
.panel-shell :deep(.panel-subsection-title) {
  font-size: 15px;
  font-weight: 700;
  color: #f4f8fc;
}

.panel-shell :deep(.panel-section-subtitle) {
  font-size: 12px;
  line-height: 1.45;
  color: #9fb7c8;
}

.panel-shell :deep(.summary-count),
.panel-shell :deep(.card-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.panel-shell :deep(.summary-count) {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d8e9f4;
}

.panel-shell :deep(.card-badge) {
  background: rgba(111, 196, 182, 0.16);
  border: 1px solid rgba(111, 196, 182, 0.3);
  color: #c9fff7;
}

.panel-shell :deep(.stacked-panel-body) {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.panel-shell :deep(.button-grid) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.panel-shell :deep(.button-grid-two) {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.panel-shell :deep(.button-grid-three) {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.panel-shell :deep(.view-actions) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.panel-shell :deep(.panel-button) {
  appearance: none;
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid rgba(141, 179, 201, 0.2);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef6fb;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 140ms ease,
    box-shadow 140ms ease;
}

.panel-shell :deep(.panel-button:hover:not(:disabled)) {
  transform: translateY(-1px);
  border-color: rgba(160, 209, 238, 0.42);
  background: rgba(124, 188, 220, 0.12);
}

.panel-shell :deep(.panel-button.active) {
  border-color: rgba(67, 220, 196, 0.72);
  background: linear-gradient(180deg, rgba(22, 92, 83, 0.92) 0%, rgba(10, 57, 52, 0.92) 100%);
  box-shadow: inset 0 0 0 1px rgba(125, 255, 232, 0.16);
}

.panel-shell :deep(.panel-button:disabled) {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.panel-shell :deep(.panel-button-wide) {
  width: 100%;
}

.panel-shell :deep(.panel-button-label) {
  display: block;
  font-size: 14px;
  font-weight: 600;
}

.panel-shell :deep(.panel-button-meta) {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #9eb7c8;
}

.panel-shell :deep(.panel-button-subtle) {
  background: rgba(255, 255, 255, 0.02);
}

.panel-shell :deep(.panel-button-neutral) {
  background: rgba(157, 176, 193, 0.12);
}

.panel-shell :deep(.panel-input) {
  width: 100%;
  min-height: 44px;
  padding: 11px 14px;
  border: 1px solid rgba(152, 191, 214, 0.18);
  border-radius: 14px;
  background: rgba(6, 10, 16, 0.52);
  color: #f3f8fc;
  font-size: 14px;
  outline: none;
}

.panel-shell :deep(.panel-input:focus) {
  border-color: rgba(121, 201, 197, 0.55);
  box-shadow: 0 0 0 3px rgba(121, 201, 197, 0.14);
}

.panel-shell :deep(.panel-note),
.panel-shell :deep(.panel-hint) {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9fb7c8;
}

.panel-shell :deep(.status-grid) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.panel-shell :deep(.status-item) {
  display: flex;
  min-height: 70px;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
}

.panel-shell :deep(.status-label) {
  font-size: 12px;
  color: #92aebf;
}

.panel-shell :deep(.status-value) {
  font-size: 16px;
  font-weight: 700;
  color: #f7fbff;
}

@media (max-width: 1280px) {
  .multi-viewport-page {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 1100px) {
  /* html/body/#app 全局 overflow: hidden，页面级滚动无法生效，
     故移动端下面板改为悬浮展示，画布始终铺满整页，无需依赖页面滚动 */
  .multi-viewport-page,
  .multi-viewport-page.panel-collapsed {
    grid-template-columns: 1fr;
    height: 100dvh;
    min-height: 100vh;
    overflow: hidden;
  }

  .stage-column {
    padding: 12px;
  }

  .stage-header {
    flex-direction: column;
  }

  .stage-pill-list {
    justify-content: flex-start;
  }

  .panel-column {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 1000;
    width: min(100vw - 20px, 340px);
    height: calc(100vh - 20px);
    max-height: calc(100vh - 20px);
    padding: 0;
  }

  .panel-expand-button {
    top: 10px;
    right: 10px;
  }
}

@media (max-width: 860px) {
  .viewport-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(240px, 1.5fr) repeat(2, minmax(160px, 1fr));
  }

  .viewport-pane-primary {
    grid-row: auto;
  }

  .panel-shell :deep(.button-grid-three) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .stage-header {
    gap: 12px;
  }

  .stage-title {
    font-size: 24px;
  }

  .viewport-layout {
    gap: 10px;
    padding: 10px;
  }

  .viewport-tag {
    top: 10px;
    right: 10px;
    max-width: calc(100% - 20px);
  }

  .viewport-hint {
    right: 10px;
    bottom: 10px;
  }

  .panel-shell-summary {
    padding: 14px 14px 12px;
  }

  .panel-shell-body {
    padding: 10px;
  }

  .panel-title {
    font-size: 20px;
  }
}

.extra-model-row {
  display: grid;
  grid-template-columns: minmax(64px, 1fr) 2fr;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-left: 3px solid var(--extra-accent, #7bd9ff);
  border-radius: 4px;
  background: rgba(12, 24, 34, 0.5);
}

.extra-model-name {
  color: #dbeaf5;
  font-size: 12px;
}
</style>
